<template>
  <StepLayout
    :back="back"
    :next="next"
    :isEditStepRoute="isEditStepRoute"
    helpscoutArticle="66588f3e077fa94da464eb8b"
    buttonDataEc="envelopes_documents_page"
  >
    <template v-slot:title>
      {{ $t('ui.envelopes.create.steps.parties.title') }}
    </template>

    <div class="flex flex-wrap gap-base items-center mb-base sm:mb-lg">
      {{ $t('ui.envelopes.create.steps.parties.signingSequenceInstruction') }}
      <div class="flex flex-wrap gap-base">
        <div class="flex gap-xs items-center">
          <VCheckbox
            :checked="!parallelSigning"
            @input="setParallelSigning(false)"
            type="radio"
          />
          {{ $t('ui.envelopes.create.steps.parties.signingSequenceInOrder') }}
        </div>
        <div class="flex gap-xs items-center">
          <VCheckbox
            :checked="parallelSigning"
            @input="setParallelSigning(true)"
            type="radio"
          />
          {{ $t('ui.envelopes.create.steps.parties.signingSequenceAllAtOnce') }}
        </div>
      </div>
    </div>

    <ol class="list-none p-0">
      <li
        v-for="(party, index) in parties"
        :key="index"
        class="mb-lg"
      >
        <PartyCard
          :party="party"
          :partyIndex="index"
          :validate="getPartyValidate(index)"
          :errors="getPartyErrors(index)"
          :canRemove="canRemoveParties"
          @remove="removeParty"
          @toggleCopyOnly="toggleCopyOnly"
          :roles="roles"
          @toggleRoleSelected="toggleRoleSelected"
          @addMyselfAsParty="addMyselfAsParty"
        />
      </li>
    </ol>

    <BaseButton
      variant="secondary"
      size="small"
      @click.native="addParty"
    >
      <span aria-hidden="true">+</span>
      {{ $t('ui.envelopes.create.steps.parties.addParty') }}
    </BaseButton>

    <p
      v-if="roles.documentsWithRoles.length"
      class="flex sm:justify-end mt-base"
    >
      <template v-if="notAllAssigned">
        <span
          class="inline-flex justify-center items-center text-white bg-primaryGradient h-twentyFive w-twentyFive mr-sm rounded-full flex-shrink-0"
          :class="{
            '!bg-warning2': !isValid && notAllAssigned,
            '!w-auto px-sm': roles.unassignedRoles > 9,
          }"
        >
          {{ roles.unassignedRoles }}
        </span>
        {{
          $tc(
            'ui.envelopes.create.steps.parties.remaining',
            roles.unassignedRoles,
          )
        }}
      </template>

      <template v-else>
        <CheckIcon
          class="text-white bg-success3 rounded-full h-base w-base p-xs mr-sm flex-shrink-0"
        />
        {{ $t('ui.envelopes.create.steps.parties.complete') }}
      </template>
    </p>

    <p
      v-if="hasErrors('parties')"
      class="flex justify-end mt-base"
    >
      <IconCircular
        class="text-warning2 border-solid border-2 border-warning2 mr-sm h-twentyFive"
      >
        <ExclamationIcon aria-hidden="true" />
      </IconCircular>
      {{ $t('ui.envelopes.create.steps.parties.errors') }}
    </p>

    <OutdatedDraftModal
      :updateError="updateErrorEnvelopeWithParties"
      @close="resetUpdateErrorEnvelopeWithParties"
    />
  </StepLayout>
</template>

<script setup>
// @ts-check
import { computed, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router/composables';

import { partiesUpdateSchema } from '@/data/schema/parties';
import {
  useEnvelope,
  useEnvelopesRoutes,
  useEnvelopesStepsNavigation,
  useUpdateEnvelopeWithParties,
} from '@/composables/envelopes';
import {
  useErrors,
  useUpdateWithValidation,
  useValidation,
} from '@/composables/envelopes/validation';
import { useParties } from '@/composables/parties/parties';
import { useAssignRoles, useRoles } from '@/composables/roles/roles';
import { useUser } from '@/composables/user';

import { CheckIcon } from '@/components/icons/fontIcons';
import StepLayout from '@/components/layouts/StepLayout/StepLayout.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import { ExclamationIcon, IconCircular } from '@/components/library/icons';
import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import { OutdatedDraftModal } from '@/components/ui/envelopeCreate/OutdatedDraftModal';
import PartyCard from '@/components/ui/envelopeCreate/PartyCard/PartyCard.vue';

/** @typedef {import('@/data/schema/parties').TCreatingParties} TCreatingParties */
/** @typedef {import('@/data/schema/parties').TNewOrSavedParty} TNewOrSavedParty */
/** @typedef {import('@/data/schema/documents').TDocuments} TDocuments */

const props = defineProps({
  currentParties: {
    /** @type {import('vue').PropType<TCreatingParties>} */
    type: Array,
    required: true,
  },
  documents: {
    /** @type {import('vue').PropType<TDocuments>} */
    type: Array,
    required: true,
  },
  parallelSigning: {
    /** @type {import('vue').PropType<boolean|null>} */
    type: Boolean,
    required: true,
  },
});
const { currentParties, documents } = toRefs(props);

const parallelSigning = ref(props.parallelSigning);

function setParallelSigning(setting) {
  parallelSigning.value = setting;
}

if (!currentParties?.value || !documents?.value) {
  throw new Error('PartiesScreen: currentParties and documents are required');
}

const route = useRoute();
const { isEditStepRoute } = useEnvelopesRoutes();

const user = useUser();

const { envelope } = useEnvelope(route.params.fingerprint);

const { parties, addParty, removeParty, canRemoveParties } = useParties(
  currentParties,
  documents,
);

const roles = useRoles(parties, documents);
const { toggleCopyOnly, toggleRoleSelected } = useAssignRoles(
  parties,
  documents,
);

/** @param {number} index */
function addMyselfAsParty(index) {
  const party = parties.value[index];

  party.title = user.value.name;
  party.email = user.value.email;
}

const {
  updateEnvelopeWithParties,
  updateErrorEnvelopeWithParties,
  resetUpdateErrorEnvelopeWithParties,
} = useUpdateEnvelopeWithParties(route.params.fingerprint);

const partiesUpdate = computed(() => ({
  parties: (parties.value || []).map((party, index) => ({
    ...party,
    order: index,
  })),
  parallel_signing: !!parallelSigning.value,
  envelope_progressed_at: envelope.value?.steps_progress?.updated_at,
}));

const notAllAssigned = computed(() => !!roles.unassignedRoles);

const { isValid, data, errors, validate } = useValidation(
  partiesUpdate,
  partiesUpdateSchema,
  notAllAssigned,
);

const { hasErrors } = useErrors(errors);

/** @param {number} index of party */
const getPartyErrors = (index) => errors.value?.parties?.[index] || undefined;
/** @param {number} index of party */
const getPartyValidate =
  (index) =>
  /** @param {string} path to validate */
  (path) =>
    validate(['parties', index, path].join('.'));

watch(partiesUpdate, () => {
  if (errors.value?.parties) {
    validate();
  }
});

const update = useUpdateWithValidation(
  updateEnvelopeWithParties,
  data,
  validate,
);

const { back, next } = useEnvelopesStepsNavigation(update, isValid);
</script>
